import {useNavigate} from 'react-router-dom';
import {useContext, useEffect, useState} from 'react';
import Instructions from '../../baseElements/Instructions';
import axios from 'axios';
import TopBar from "../../baseElements/TopBar";
import {activity_palettes} from "../../themes";
import ConfirmButton from "../../baseElements/ConfirmButton";
import SadFeedback from "../../baseElements/SadFeedback";
import HappyFeedback from "../../baseElements/HappyFeedback";
import WordBuildCard from "../../baseElements/WordBuildCard";
import SolutionWord from "../../baseElements/SolutionWord";
import AnimationOverlay from "../../baseElements/AnimationOverlay";
import {AppContext} from "../../AppContext";
import {checkActivityStatus, delay, generateUrl, getExerciseFromDB, getLog, randomizeWords, redirectToExercise, updateCurrentLog} from "../../ActivityManager";
import NextExerciseButton from "../../baseElements/NextExerciseButton";
import Spinner from "react-bootstrap/Spinner";

axios.defaults.withCredentials = true;
const BACKGROUND_STYLE = activity_palettes.green.primary;
const INSTRUCTION_TEXT = "Quante nuove parole riesci a formare? Prova a unire i blocchi per formare il maggior numero di parole possibili.";

function WordBuild() {
    const {activityList, setActivityList, currentActivity} = useContext(AppContext);
    
    const navigate = useNavigate();
    
    const [words, setWords] = useState([]);
    const [maxSteps, setMaxSteps] = useState(0);
    const [stepInfo, setStepInfo] = useState(0);
    const [levelInfo, setLevelInfo] = useState(0);
    const [instructionAudioUrl, setInstructionAudioUrl] = useState('');
    const [loading, setLoading] = useState(true);
    
    const [solutionBackgroundColor, setSolutionBackgroundColor] = useState('#f9f9f9');
    const [solutionName, setSolutionName] = useState('');
    const [clickedWordsId, setClickedWordsId] = useState([]);
    const [confirmedWords, setConfirmedWords] = useState([]);
    const [feedback, setFeedback] = useState('');
    const [correctWords, setCorrectWords] = useState([]);
    const [errorWords, setErrorWords] = useState([]);
    
    const [isAnimating, setIsAnimating] = useState(false);
    const [, setIsMdOrLess] = useState(window.innerWidth <= 768);
    
    const [flowButtons, setFlowButtons] = useState({confirm: true, nextExercise: false});
    const [exerciseLog, setExerciseLog] = useState({exercise_id: 0, exercise_type: '', progress: 0, actions: []});
    
    const exerciseId = activityList[0][0];
    const exerciseType = activityList[0][1];
    
    function updateButtonFlow(showConfirm, showNextExercise) {
        setFlowButtons({confirm: showConfirm, nextExercise: showNextExercise});
    }
    
    function resetStates() {
        setWords([]);
        setMaxSteps(0);
        setStepInfo(0);
        setSolutionBackgroundColor('#f9f9f9');
        setSolutionName('');
        setClickedWordsId([]);
        setConfirmedWords([]);
        setFeedback('');
        setCorrectWords([]);
        setErrorWords([]);
        setIsAnimating(false);
        setLoading(true);
        updateButtonFlow(true, false);
    }
    
    useEffect(() => {
        window.history.pushState(null, '', window.location.href);
        
        window.onpopstate = (event) => {
            event.preventDefault();
            navigate('/home'); // Navigate directly to /home
        };
        
        return () => {
            window.onpopstate = null;
        };
    }, [navigate]);
    
    const handleResize = () => {
        setIsMdOrLess(window.innerWidth < 768);
    };
    
    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    
    useEffect(() => {
        resetStates();
        loadExercise()
            .then((wordsTemporary) => {
                if (wordsTemporary.length > 0) {
                    syncExerciseWithDB(wordsTemporary).then(() => setLoading(false));
                } else {
                    console.error('No words loaded');
                    setLoading(false);
                }
            })
            .catch((error) => {
                console.error('Error during loading or syncing:', error);
                setLoading(false);
            });
    }, [navigate]);
    
    async function loadExercise() {
        await delay(750);
        return getExerciseFromDB(activityList)
            .then((response) => {
                if (response.success) {
                    const json = JSON.parse(JSON.stringify(response.data[4]));
                    const level = JSON.parse(JSON.stringify(response.data[3]));
                    let wordsTemporary = [];
                    json.word_building.words.forEach((activity) => {
                        if (activity.type === 'prefix') {
                            wordsTemporary.push({id: activity.wordId, type: activity.type, name: activity.wordName, suffix: activity.suffix, solution: activity.solution});
                        } else if (activity.type === 'suffix') {
                            wordsTemporary.push({id: activity.wordId, type: activity.type, name: activity.wordName});
                        }
                    });
                    
                    generateUrl("word building").then((url) => {
                        setInstructionAudioUrl(url)
                    });
                    
                    wordsTemporary = randomizeWords(wordsTemporary);
                    
                    setWords(wordsTemporary);
                    setMaxSteps(wordsTemporary.length / 2);
                    setLevelInfo(level);
                    
                    return wordsTemporary;
                } else {
                    console.log(response.errorMess);
                    return [];
                }
            })
            .catch((error) => {
                console.log(error);
                return [];
            });
    }
    
    function syncExerciseWithDB(wordsTemporary) {
        return new Promise((resolve) => {
            getLog(currentActivity).then((response) => {
                if (response.success) {
                    const exercisesString = JSON.stringify(response.data);
                    const exercises = JSON.parse(exercisesString)[0];
                    if (exercises.some((exercise) => exercise.exercise_id === exerciseId)) {
                        const exercise = exercises.find((exercise) => exercise.exercise_id === exerciseId);
                        let previousSelectedWords = [];
                        setExerciseLog(exercise);
                        exercise.actions.forEach((action) => {
                            if (action.correct) {
                                previousSelectedWords = [...action.selectedWords, ...previousSelectedWords];
                                setStepInfo(Math.round((exercise.progress / 100) * (wordsTemporary.length / 2)));
                            }
                        });
                        setCorrectWords(previousSelectedWords);
                    }
                } else {
                    console.log(response.data.errorMess);
                }
                resolve();
            });
        });
    }
    
    function logUserAction(correct, progress, words) {
        const timestamp = new Date().toISOString();
        const newAction = {
            selectedWords: words,
            correct: correct,
            timestamp: timestamp,
        };
        
        console.log(newAction);
        
        setExerciseLog((prev) => ({
            ...prev,
            exercise_id: exerciseId,
            exercise_type: exerciseType,
            progress: Math.round(progress),
            actions: [...prev.actions, newAction],
        }));
    }
    
    function handleConfirmClick() {
        if(clickedWordsId.length === 2) {
            setConfirmedWords([...clickedWordsId]);
            setClickedWordsId([]);
        }
    }
    
    function handleNextExerciseClick() {
        const temp = [...activityList];
        temp.shift();
        checkActivityStatus(currentActivity, temp).then(
            (response) => {
                try {
                    setActivityList(response);
                    redirectToExercise(response, navigate);
                } catch (error) {
                    console.log(error);
                }
            }
        );
    }
    
    const handleWordClick = (word) => {
        if (clickedWordsId.some((word1) => word1.id === word.id)) {
            setClickedWordsId(clickedWordsId.filter(clickedWord => clickedWord.id !== word.id));
        } else if (clickedWordsId.length < 2) {
            setClickedWordsId((prev) => [word, ...prev]);
        }
    };
    
    function handleIncorrect() {
        setFeedback('sad');
        setErrorWords((prev) => [...confirmedWords, ...prev]);
        setSolutionName(confirmedWords.map((word) => word.name).reverse().join(' / '));
        setSolutionBackgroundColor(activity_palettes.green.error_background);
        
        setTimeout(() => {
            setErrorWords([]);
            setConfirmedWords([]);
        }, 1250);
    }
    
    useEffect(() => {
        if (correctWords.length === words.length && words.length !== 0) {
            setIsAnimating(true);
            setTimeout(() => {
                updateButtonFlow(false, true);
                setIsAnimating(false);
            }, 1250);
        } else {
            updateButtonFlow(true, false);
        }
    }, [correctWords]);
    
    useEffect(() => {
        if (confirmedWords.length !== 2) return;
        setIsAnimating(true);
        let isCorrect = false;
        const hasPrefixAndSuffix = confirmedWords.some((word) => word.type === 'prefix') && confirmedWords.some((word) => word.type === 'suffix');
        
        if (hasPrefixAndSuffix) {
            const prefix = confirmedWords.find((word) => word.type === 'prefix');
            const suffix = confirmedWords.find((word) => word.type === 'suffix');
            isCorrect = prefix.suffix === suffix.name;
            
            if (isCorrect) {
                setCorrectWords((prev) => [...confirmedWords, ...prev]);
                setFeedback('happy');
                setSolutionName(prefix.solution);
                setSolutionBackgroundColor(activity_palettes.green.tertiary);
                setStepInfo(stepInfo + 1);
            } else {
                handleIncorrect();
            }
        } else {
            handleIncorrect();
        }
        
        const dynamicCorrectWordsCount = isCorrect ? correctWords.length + confirmedWords.length : correctWords.length;
        const progress = (dynamicCorrectWordsCount / words.length) * 100;
        logUserAction(isCorrect, progress, confirmedWords);
        
        setTimeout(() => {
            setFeedback('');
            setSolutionName('');
            setSolutionBackgroundColor('#f9f9f9');
            setIsAnimating(false);
        }, 1250);
        
    }, [confirmedWords]);
    
    useEffect(() => {
        if (exerciseLog.exercise_id !== 0) {
            updateCurrentLog(currentActivity, exerciseLog).then();
        }
    }, [exerciseLog]);
    
    if (loading) {
        return (
            <div className='generalActivitiesBackground custom-col-12 tablet-custom-col-12' style={{backgroundColor: BACKGROUND_STYLE}}>
                <Spinner animation="border" variant="light"/>
            </div>
        );
    }
    
    return (
        <div className='generalActivitiesBackground custom-col-12 tablet-custom-col-12' style={{backgroundColor: BACKGROUND_STYLE}}>
            <AnimationOverlay isAnimating={isAnimating}></AnimationOverlay>
            <div className='generalActivitiesPageContent custom-col-11 tablet-custom-col-11'>
                <div className='generalActivitiesTopBarArea custom-row'>
                    <TopBar levelInfo={levelInfo} stepInfo={stepInfo} maxSteps={maxSteps}> </TopBar>
                    <Instructions hasAudio={false} instructionText={INSTRUCTION_TEXT} instructionAudioUrl={instructionAudioUrl}></Instructions>
                </div>
                <div className='generalActivitiesGameElementsArea row'>
                    <div className='generalActivitiesFeedbackArea custom-col-2 tablet-custom-col-2'>
                        <SadFeedback feedback={feedback.includes('sad')}></SadFeedback>
                    </div>
                    <div className='generalActivitiesColumnOneColumnLayout custom-col-8 tablet-custom-col-8'>
                        <div className='word-build-grid'>
                            {words.map((word) => {
                                let zoom = 1;
                                let backgroundColor = '#f9f9f9';
                                if (confirmedWords.some((confirmedWord) => confirmedWord.id === word.id)) {
                                    if (confirmedWords.every((word) => word.couple_id === confirmedWords[0].couple_id)) {
                                        backgroundColor = activity_palettes.green.tertiary;
                                    } else if (errorWords.some((errorWord) => errorWord.id === word.id)) {
                                        zoom = 1.015;
                                        backgroundColor = activity_palettes.green.error_background;
                                    }
                                } else if (clickedWordsId.some((clickedWord) => clickedWord.id === word.id)) {
                                    zoom = 1.015;
                                    backgroundColor = activity_palettes.green.secondary;
                                }
                                return (
                                    <WordBuildCard
                                        key={word.id}
                                        name={word.name}
                                        backgroundColor={backgroundColor}
                                        onClick={() => handleWordClick(word)}
                                        isFlipping={correctWords.some((correctWord) => correctWord.id === word.id)}
                                        zoom={zoom}
                                    />
                                );
                            })}
                        </div>
                        <SolutionWord name={solutionName} backgroundColor={solutionBackgroundColor}/>
                    </div>
                    <div className='generalActivitiesFeedbackArea custom-col-2 tablet-custom-col-2'>
                        <HappyFeedback feedback={feedback.includes('happy')}></HappyFeedback>
                    </div>
                
                </div>
                <div className='generalActivitiesConfirmButtonArea custom-row'>
                    {flowButtons.confirm && <ConfirmButton onClick={handleConfirmClick}/>}
                    {flowButtons.nextExercise && <NextExerciseButton onClick={handleNextExerciseClick}/>}
                </div>
            </div>
        </div>
    );
}

export default WordBuild;
